/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * copilotEventSchemas.ts — copilo\1 \2ven\1 \2chemas (services/operator-web-app).
 */

/**
 * E6-S4: Runtime validation of AI (copilot) event payloads.
 * Invalid payloads are rejected so the reducer does not apply malformed data.
 */

const SPEAKERS = new Set<string>(['client', 'operator', 'bot']);
const HIGHLIGHT_WIDGETS = new Set<string>([
  'customer_card_header',
  'customer_card_fields',
  'calendar_widget',
  'map_widget',
  'wrap_up_panel',
  'notes_widget',
]);
const HIGHLIGHT_LEVELS = new Set<string>(['soft', 'medium', 'critical']);

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isString(v: unknown): v is string {
  return typeof v === 'string';
}

function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export interface AsrPartialPayload {
  speaker: 'client' | 'operator' | 'bot';
  text: string;
  result_id: string;
}

export function validateAsrPartial(
  payload: unknown
): { ok: true; payload: AsrPartialPayload } | { ok: false; reason: string } {
  if (!isObject(payload)) return { ok: false, reason: 'payload is not an object' };
  if (!SPEAKERS.has(payload.speaker as string)) return { ok: false, reason: 'invalid speaker' };
  if (!isString(payload.text)) return { ok: false, reason: 'text must be string' };
  if (!isString(payload.result_id)) return { ok: false, reason: 'result_id must be string' };
  return {
    ok: true,
    payload: {
      speaker: payload.speaker as 'client' | 'operator' | 'bot',
      text: payload.text,
      result_id: payload.result_id,
    },
  };
}

export interface AsrFinalPayload {
  speaker: 'client' | 'operator' | 'bot';
  text: string;
  result_id: string;
  confidence?: number;
}

export function validateAsrFinal(
  payload: unknown
): { ok: true; payload: AsrFinalPayload } | { ok: false; reason: string } {
  if (!isObject(payload)) return { ok: false, reason: 'payload is not an object' };
  if (!SPEAKERS.has(payload.speaker as string)) return { ok: false, reason: 'invalid speaker' };
  if (!isString(payload.text)) return { ok: false, reason: 'text must be string' };
  if (!isString(payload.result_id)) return { ok: false, reason: 'result_id must be string' };
  if (payload.confidence !== undefined && !isNumber(payload.confidence))
    return { ok: false, reason: 'confidence must be number' };
  return {
    ok: true,
    payload: {
      speaker: payload.speaker as 'client' | 'operator' | 'bot',
      text: payload.text,
      result_id: payload.result_id,
      confidence: payload.confidence as number | undefined,
    },
  };
}

export interface HintGeneratedPayload {
  hint_id: string;
  text: string;
  intent: string;
  confidence: number;
  why?: string;
  actions?: Array<'copy' | 'insert_note' | 'apply_field' | 'mark_used'>;
}

export function validateHintGenerated(
  payload: unknown
): { ok: true; payload: HintGeneratedPayload } | { ok: false; reason: string } {
  if (!isObject(payload)) return { ok: false, reason: 'payload is not an object' };
  if (!isString(payload.hint_id)) return { ok: false, reason: 'hint_id must be string' };
  if (!isString(payload.text)) return { ok: false, reason: 'text must be string' };
  if (!isString(payload.intent)) return { ok: false, reason: 'intent must be string' };
  if (!isNumber(payload.confidence)) return { ok: false, reason: 'confidence must be number' };
  if (payload.why !== undefined && !isString(payload.why))
    return { ok: false, reason: 'why must be string' };
  if (payload.actions !== undefined) {
    if (!Array.isArray(payload.actions)) return { ok: false, reason: 'actions must be array' };
    const allowed = new Set(['copy', 'insert_note', 'apply_field', 'mark_used']);
    if (!payload.actions.every((a: unknown) => typeof a === 'string' && allowed.has(a))) {
      return { ok: false, reason: 'actions must be valid action strings' };
    }
  }
  return {
    ok: true,
    payload: {
      hint_id: payload.hint_id,
      text: payload.text,
      intent: payload.intent,
      confidence: payload.confidence,
      why: payload.why as string | undefined,
      actions: payload.actions as HintGeneratedPayload['actions'],
    },
  };
}

export interface HighlightRecommendedPayload {
  target_widget:
    | 'customer_card_header'
    | 'customer_card_fields'
    | 'calendar_widget'
    | 'map_widget'
    | 'wrap_up_panel'
    | 'notes_widget';
  level: 'soft' | 'medium' | 'critical';
  reason: string;
  ttl_ms: number;
}

export function validateHighlightRecommended(
  payload: unknown
): { ok: true; payload: HighlightRecommendedPayload } | { ok: false; reason: string } {
  if (!isObject(payload)) return { ok: false, reason: 'payload is not an object' };
  if (!HIGHLIGHT_WIDGETS.has(payload.target_widget as string))
    return { ok: false, reason: 'invalid target_widget' };
  if (!HIGHLIGHT_LEVELS.has(payload.level as string)) return { ok: false, reason: 'invalid level' };
  if (!isString(payload.reason)) return { ok: false, reason: 'reason must be string' };
  if (!isNumber(payload.ttl_ms) || payload.ttl_ms < 0)
    return { ok: false, reason: 'ttl_ms must be non-negative number' };
  return {
    ok: true,
    payload: {
      target_widget: payload.target_widget as HighlightRecommendedPayload['target_widget'],
      level: payload.level as HighlightRecommendedPayload['level'],
      reason: payload.reason,
      ttl_ms: payload.ttl_ms,
    },
  };
}

export type CopilotEventType =
  | 'asr_partial'
  | 'asr_final'
  | 'hint_generated'
  | 'highlight_recommended';

export function validateCopilotPayload(
  eventType: CopilotEventType,
  payload: unknown
):
  | {
      ok: true;
      payload:
        | AsrPartialPayload
        | AsrFinalPayload
        | HintGeneratedPayload
        | HighlightRecommendedPayload;
    }
  | { ok: false; reason: string } {
  switch (eventType) {
    case 'asr_partial':
      return validateAsrPartial(payload);
    case 'asr_final':
      return validateAsrFinal(payload);
    case 'hint_generated':
      return validateHintGenerated(payload);
    case 'highlight_recommended':
      return validateHighlightRecommended(payload);
    default:
      return { ok: false, reason: `unknown event type: ${eventType}` };
  }
}
